import { Link, useParams } from '@tanstack/react-router';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { AgencyLink } from '~/common/types';
import { AUTO_COL_WIDTH, TableV2 } from '~/components2/Table';
import store from '../../../../store';

interface AgencyLinkItem {
  agencyName: string;
  link: AgencyLink;
}

const columns: ColumnDef<AgencyLinkItem>[] = [
  {
    id: 'doc',
    header: 'Documents',
    accessorKey: 'agencyName',
    size: AUTO_COL_WIDTH,
    cell: (cell) => {
      const { link, agencyName } = cell.row.original;

      return (
        <div className="space-y-2 text-left">
          <div>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-50 underline hover:text-blue-60"
            >
              {link.title}
            </a>
          </div>
          <div>{link.description}</div>
          <div>
            <span className="font-medium">{agencyName}</span>
          </div>
        </div>
      );
    },
  },
];

export default function Links() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { agencies, formState } = useSnapshot(store);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const data = useMemo(() => {
    const docs: AgencyLinkItem[] = [];

    Object.values(agencies).forEach((a) => {
      if (formState.mapsSelection.has(a.id) || formState.asbuiltsSelection.has(a.id)) {
        a.links.forEach((link) => {
          const item = { agencyName: `${a.agencyName} (${a.type})`, link };
          docs.push(item);
        });
      }
    });

    return docs.sort((a, b) => a.agencyName.localeCompare(b.agencyName));
  }, [agencies]);

  const docsTable = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isEmpty(data)) {
    return null;
  }

  return (
    <div className="mb-4 space-y-4">
      <div>
        To request data from the selected agencies on your behalf, the following documentation is required. While we
        understand that completing these documents may take some time, we strongly recommend submitting them in full
        before initiating a request to ensure a smooth and timely process If you have any questions, contact us using
        the{' '}
        <Link
          to="/projects/$projectId/$tabId"
          params={{ projectId, tabId: 'utilities' }}
          search={{ drawer: 'project-channel' }}
        >
          Project Channel
        </Link>{' '}
        button in the top right.
      </div>

      <TableV2 id="utilities-links-contacts-v1" table={docsTable} />
    </div>
  );
}
